// The AssemblyAI streaming model every live transcript uses: the simulators,
// Guide (the server hands it to the iOS and Android apps), and dictation.
// "u3-rt-pro" has routed to this model since Aug 7, 2026, and AssemblyAI
// retires that id around Sep 25, 2026, so this is the only spelling to send.
export const STREAMING_SPEECH_MODEL = "universal-3-5-pro";

const DEFAULT_U3_STREAMING_PROMPT = [
  "Transcribe verbatim.",
  "Rules:",
  "Always include punctuation in output.",
  "Use period/question mark ONLY for complete sentences.",
  "Use comma for mid-sentence pauses.",
  "Use no punctuation for incomplete trailing speech.",
  "Include spoken filler words, hesitations, repetitions and false starts when clearly spoken.",
].join(" ");

const LANGUAGE_LABELS = {
  en: "English",
  es: "Spanish",
  de: "German",
  fr: "French",
  pt: "Portuguese",
  it: "Italian",
  multi:
    "multilingual conversation in English, Spanish, German, French, Portuguese, and Italian",
};

// AssemblyAI keyterm limits (SAL-2817): streaming accepts up to 100 keyterms
// per session (more than 100 errors the connection) of up to 50 characters
// each. Batch keyterms additionally cap phrases at 6 words, so the stricter
// word limit is applied everywhere to keep one list valid for both APIs.
const MAX_KEYTERMS = 100;
const MAX_KEYTERM_LENGTH = 50;
const MAX_KEYTERM_WORDS = 6;

function normalizeText(value) {
  return typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";
}

function truncateText(value, maxLength = 140) {
  const normalized = normalizeText(value);
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 3).trim()}...`;
}

function pushKeyterm(keyterms, seen, value) {
  const normalized = normalizeText(value);
  if (!normalized) return;
  if (normalized.length > MAX_KEYTERM_LENGTH) return;

  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length > MAX_KEYTERM_WORDS) return;

  const dedupeKey = normalized.toLowerCase();
  if (seen.has(dedupeKey)) return;

  seen.add(dedupeKey);
  keyterms.push(normalized);
}

function addCandidateTerms(keyterms, seen, value) {
  if (Array.isArray(value)) {
    value.forEach((item) => addCandidateTerms(keyterms, seen, item));
    return;
  }

  if (value && typeof value === "object") {
    if (typeof value.term === "string") {
      pushKeyterm(keyterms, seen, value.term);
    }
    return;
  }

  const normalized = normalizeText(value);
  if (!normalized) return;

  // List-ish fields ("Gong, Chorus; Salesloft") become one keyterm per entry.
  // The length/word guards in pushKeyterm drop prose fragments that survive
  // the split, keeping the list term-shaped.
  normalized
    .split(/[\n,;|]/)
    .map((part) => normalizeText(part))
    .filter(Boolean)
    .forEach((part) => pushKeyterm(keyterms, seen, part));
}

function buildLanguageInstruction(language) {
  const normalizedLanguage = normalizeText(language).toLowerCase();
  if (!normalizedLanguage) return "";

  const label = LANGUAGE_LABELS[normalizedLanguage];
  if (label) {
    return `Transcribe ${label}.`;
  }

  return normalizedLanguage.length <= 40
    ? `Transcribe ${normalizedLanguage}.`
    : "";
}

function buildScenarioLabel(scenario) {
  const candidates = [
    scenario?.name,
    scenario?.trackName,
    scenario?.pitch,
    scenario?.objection,
    scenario?.situationBackground,
  ];

  for (const candidate of candidates) {
    const normalized = truncateText(candidate, 120);
    if (normalized) {
      return normalized;
    }
  }

  return "";
}

function buildPromptContext({ workspace, scenario, persona, simulationType }) {
  const parts = [];

  if (simulationType) {
    parts.push(`${simulationType} sales roleplay`);
  } else {
    parts.push("sales roleplay");
  }

  if (workspace?.name) {
    parts.push(`studio ${workspace.name}`);
  }

  if (workspace?.productMarketCategory) {
    parts.push(
      `offering ${truncateText(workspace.productMarketCategory, 90)}`
    );
  }

  if (workspace?.valueProp) {
    parts.push(`value proposition ${truncateText(workspace.valueProp, 90)}`);
  }

  const personaLabel = normalizeText(persona?.name || persona?.class);
  if (personaLabel) {
    parts.push(`buyer ${personaLabel}`);
  }

  const scenarioLabel = buildScenarioLabel(scenario);
  if (scenarioLabel) {
    parts.push(`scenario ${scenarioLabel}`);
  }

  return `This is a ${parts.join("; ")}.`;
}

function capKeyterms(keyterms, { maxTerms, maxTotalWords }) {
  const capped = [];
  let wordBudget = Number.isFinite(maxTotalWords) ? maxTotalWords : Infinity;

  for (const term of keyterms) {
    if (capped.length >= maxTerms) break;
    const wordCount = term.split(/\s+/).filter(Boolean).length;
    if (wordCount > wordBudget) continue;
    wordBudget -= wordCount;
    capped.push(term);
  }

  return capped;
}

// Keyterm biasing for AssemblyAI's `keyterms_prompt` (SAL-2817: transcription
// accuracy hurts scoring). AssemblyAI guidance: keyterms should be unique
// terminology with exact spelling — product names, proper nouns, domain terms —
// while common words and prose fragments risk overcorrections. Sources are
// therefore ranked so the studio's curated vocabulary (glossary terms and
// preferred terminology — the words scoring later checks for) always makes the
// list ahead of persona/scenario labels, and prose positioning fields (value
// prop, company values, pain points) are left to the contextual `prompt`,
// which is where AssemblyAI wants descriptive context.
//
// `maxTotalWords` exists for batch (pre-recorded) callers: batch keyterm
// capacity is measured in words, with Universal-2 supporting ~200.
export function collectWorkspaceKeyterms({
  workspace,
  scenario,
  persona,
  glossaryTerms,
  maxTerms = MAX_KEYTERMS,
  maxTotalWords = null,
}) {
  const keyterms = [];
  const seen = new Set();

  // 1. Curated studio vocabulary — the terms feedback scoring depends on.
  const glossarySource =
    Array.isArray(glossaryTerms) && glossaryTerms.length > 0
      ? glossaryTerms
      : Array.isArray(scenario?.glossaryGroup?.terms)
        ? scenario.glossaryGroup.terms
        : [];
  addCandidateTerms(
    keyterms,
    seen,
    glossarySource.map((entry) => entry?.term || entry)
  );
  addCandidateTerms(keyterms, seen, workspace?.newWords);

  // 2. Product / company proper nouns.
  addCandidateTerms(keyterms, seen, workspace?.name);
  addCandidateTerms(keyterms, seen, workspace?.competitiveAlternatives);
  addCandidateTerms(keyterms, seen, workspace?.productMarketCategory);
  addCandidateTerms(keyterms, seen, workspace?.uniqueAttributes);

  // 3. Names likely spoken during the conversation.
  addCandidateTerms(keyterms, seen, persona?.name);
  addCandidateTerms(keyterms, seen, scenario?.name);
  addCandidateTerms(keyterms, seen, scenario?.trackName);

  return capKeyterms(keyterms, { maxTerms, maxTotalWords });
}

export function buildUniversal3StreamingConfig({
  language,
  recordingMode,
  workspace,
  scenario,
  persona,
  glossaryTerms,
  simulationType,
}) {
  const promptParts = [];
  const languageInstruction = buildLanguageInstruction(language);

  if (languageInstruction) {
    promptParts.push(languageInstruction);
  }

  promptParts.push(DEFAULT_U3_STREAMING_PROMPT);
  promptParts.push(
    buildPromptContext({ workspace, scenario, persona, simulationType })
  );
  promptParts.push(
    "Prefer exact transcription of product names, studio terminology, proper nouns, numbers, email addresses, and alphanumeric identifiers when spoken."
  );

  // Turn-detection (endpointing) silence thresholds for AssemblyAI
  // Universal-3.5 Pro Streaming. `min_turn_silence` is the pause before the
  // model runs an end-of-turn check (it only ends the turn if the content also
  // reads as complete); `max_turn_silence` is the hard cap that force-ends a
  // turn regardless of content.
  //
  // "regular" (Practice) is manual push-to-talk — the user taps to submit, so
  // endpointing only groups finals and never auto-responds; its values are
  // intentionally left as-is.
  //
  // The non-regular branch covers the hands-free auto-submit modes (Scored
  // "vad" and Live Mic "livemic"), where an end-of-turn arms an auto-submit
  // that makes the persona respond. The previous values (100 / 1000) were more
  // aggressive than even AssemblyAI's "aggressive/IVR" preset, so brief
  // mid-sentence pauses in reflective sales speech read as end-of-turn and the
  // persona talked over reps who were still speaking. These match AssemblyAI's
  // documented conservative preset for "reflective/complex speech (healthcare,
  // sales, legal)" (min 800; max between the balanced 1280 and conservative
  // 3600). Because the model is content-aware, a genuinely complete thought
  // still ends promptly at ~800 ms, while the higher max keeps the turn open
  // when a rep pauses mid-incomplete-sentence.
  return {
    speechModel: STREAMING_SPEECH_MODEL,
    minTurnSilence: recordingMode === "regular" ? 200 : 800,
    maxTurnSilence: recordingMode === "regular" ? 1400 : 2400,
    prompt: promptParts.join(" ").replace(/\s+/g, " ").trim(),
    keytermsPrompt: collectWorkspaceKeyterms({
      workspace,
      scenario,
      persona,
      glossaryTerms,
    }),
  };
}
